import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

export default function RegistrarVacacionManual({ usuario, token, onExito, info }) {
    const [form, setForm] = useState({
        fecha_inicio: '',
        fecha_regreso: '',
        tipo_solicitud: 'normal',
        estado: 'Aprobada',
        comentario: '',
        jefe_nombre: '',
    });
    const [dias, setDias] = useState(0);
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');
    const [sabadosDetectados, setSabadosDetectados] = useState([]);
    const [sabadosLaborables, setSabadosLaborables] = useState([]);
    const tieneDiasPendientes = (info?.dias_periodo_pasado ?? 0) > 0;
    const set = campo => valor => setForm(f => ({ ...f, [campo]: valor }));

    useEffect(() => {
        if (!usuario?.jefe_id) return;

        fetch(`${API_URL}/usuarios/${usuario.jefe_id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(res => {
                const jefe = Array.isArray(res) ? res[0] : res;
                if (jefe?.nombre_completo) {
                    setForm(prevForm => ({
                        ...prevForm,
                        jefe_nombre: jefe.nombre_completo
                    }));
                }
            })
            .catch(err => console.error('Error al obtener el jefe:', err));
    }, [usuario?.jefe_id, token]);

    // Calcula días y detecta sábados
    useEffect(() => {
        if (!form.fecha_inicio || !form.fecha_regreso) {
            setDias(0); setSabadosDetectados([]); setSabadosLaborables([]);
            return;
        }
        const [yi, mi, di] = form.fecha_inicio.split('-').map(Number);
        const [yf, mf, df] = form.fecha_regreso.split('-').map(Number);
        const inicio = new Date(yi, mi - 1, di);
        const fin = new Date(yf, mf - 1, df);

        const sabados = [];
        let actual = new Date(inicio);
        while (actual < fin) {
            if (actual.getDay() === 6) {
                const fs = `${actual.getFullYear()}-${String(actual.getMonth() + 1).padStart(2, '0')}-${String(actual.getDate()).padStart(2, '0')}`;
                sabados.push(fs);
            }
            actual.setDate(actual.getDate() + 1);
        }
        setSabadosDetectados(sabados);
        setSabadosLaborables(prev => prev.filter(s => sabados.includes(s)));
    }, [form.fecha_inicio, form.fecha_regreso]);

    // Recalcula días con sábados
    useEffect(() => {
        if (!form.fecha_inicio || !form.fecha_regreso) { setDias(0); return; }
        fetch(`${API_URL}/vacaciones/calcular-dias`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
                fecha_inicio: form.fecha_inicio,
                fecha_regreso: form.fecha_regreso,
                sabados_laborables: sabadosLaborables
            })
        })
            .then(r => r.json())
            .then(d => setDias(d.dias ?? 0))
            .catch(() => { });
    }, [form.fecha_inicio, form.fecha_regreso, sabadosLaborables]);

    const toggleSabado = (fecha) => {
        setSabadosLaborables(prev =>
            prev.includes(fecha) ? prev.filter(s => s !== fecha) : [...prev, fecha]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setEnviando(true);
        try {
            const res = await fetch(`${API_URL}/vacaciones/registrar-manual`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    ...form,
                    usuario_id: usuario.id,
                    sabados_laborables: sabadosLaborables,
                    jefe_nombre: form.jefe_nombre,
                })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message);
            onExito();
        } catch (err) {
            setError(err.message);
        } finally {
            setEnviando(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div style={{
                background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6,
                padding: '8px 12px', fontSize: 12, color: '#795548', marginBottom: 14
            }}>
                📋 Este registro lo realiza Capital Humano en nombre del colaborador.
                La autorización fue dada por el jefe directo fuera del sistema.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                    <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Fecha inicio</label>
                    <input className="form-input" type="date" value={form.fecha_inicio}
                        onChange={e => set('fecha_inicio')(e.target.value)} required />
                </div>
                <div>
                    <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Fecha regreso</label>
                    <input className="form-input" type="date" value={form.fecha_regreso}
                        min={form.fecha_inicio} onChange={e => set('fecha_regreso')(e.target.value)} required />
                </div>
            </div>

            {/* Días calculados */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 14px', background: '#f0f4ff', border: '1px solid #c5d3f5',
                borderRadius: 6, marginBottom: 12
            }}>
                <span style={{ fontSize: 13, color: '#444' }}>Días hábiles</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: '#1e3a5f' }}>{dias}</span>
            </div>

            {/* Sábados detectados */}
            {sabadosDetectados.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>
                        Sábados detectados en el periodo:
                    </div>
                    {sabadosDetectados.map(fecha => {
                        const laborable = sabadosLaborables.includes(fecha);
                        const [y, m, d] = fecha.split('-').map(Number);
                        const label = new Date(y, m - 1, d).toLocaleDateString('es-MX', {
                            weekday: 'long', day: 'numeric', month: 'long'
                        });
                        return (
                            <div key={fecha} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 12px', borderRadius: 6, marginBottom: 6,
                                background: laborable ? '#e8f5e9' : '#f5f5f5',
                                border: `1px solid ${laborable ? '#a5d6a7' : '#ddd'}`
                            }}>
                                <span style={{ fontSize: 12, color: '#444' }}>
                                    📅 {label.charAt(0).toUpperCase() + label.slice(1)}
                                </span>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <span style={{ fontSize: 11, color: '#888' }}>
                                        {laborable ? 'Laborable' : 'No laborable'}
                                    </span>
                                    <div onClick={() => toggleSabado(fecha)} style={{
                                        width: 40, height: 22, borderRadius: 11,
                                        background: laborable ? '#28a745' : '#ccc',
                                        position: 'relative', cursor: 'pointer', transition: 'background 0.2s'
                                    }}>
                                        <div style={{
                                            width: 18, height: 18, borderRadius: '50%', background: 'white',
                                            position: 'absolute', top: 2,
                                            left: laborable ? 20 : 2, transition: 'left 0.2s'
                                        }} />
                                    </div>
                                </label>
                            </div>
                        );
                    })}
                </div>
            )}

            <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tipo</label>
                <select className="form-input" value={form.tipo_solicitud}
                    onChange={e => set('tipo_solicitud')(e.target.value)}>
                    <option value="normal">Normal</option>
                    <option value="anticipo">Anticipo del siguiente periodo</option>
                    <option value="descuento_nomina">Descuento de nómina</option>
                    {tieneDiasPendientes && (
                        <option value="pendientes">
                            Uso de días pendientes ({info.dias_periodo_pasado} disponibles)
                        </option>
                    )}
                </select>
            </div>

            <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Estado</label>
                <select className="form-input" value={form.estado}
                    onChange={e => set('estado')(e.target.value)}>
                    <option value="Aprobada">Aprobada</option>
                    <option value="Pendiente">Pendiente</option>
                </select>
            </div>

            {/* Jefe directo */}
            <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                    Jefe que autorizó <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                    className="form-input"
                    placeholder="Nombre del autorizante"
                    value={form.jefe_nombre}
                    onChange={e => set('jefe_nombre')(e.target.value)}
                    required
                />
                {form.jefe_nombre && (
                    <span style={{ fontSize: 11, color: '#28a745' }}>
                        ✓ Precargado del sistema
                    </span>
                )}
            </div>

            <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                    Observaciones
                </label>
                <textarea className="form-input" rows={2}
                    placeholder="Registro manual por Capital Humano..."
                    value={form.comentario} onChange={e => set('comentario')(e.target.value)} />
            </div>

            {error && (
                <div style={{ fontSize: 12, color: '#dc3545', marginBottom: 10 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="submit" className="btn-primary" disabled={enviando || dias <= 0}>
                    {enviando ? 'Registrando...' : 'Registrar vacación'}
                </button>
            </div>
        </form>
    );
}